import HelpService from "../service/help.service";

const intersect = require('ray-plane-intersection');
const every = require('lodash/every');

export default class CubemapUtil {
    static RADIAN = Math.PI / 180;
    static PLANES = [
        //front
        [[0, -1, 0], 1],
        //back
        [[0, 1, 0], 1],
        //left
        [[1, 0, 0], 1],
        //right
        [[-1, 0, 0], 1],
        //top
        [[0, 0, -1], 1],
        //bottom
        [[0, 0, 1], 1]
    ];


    /**
     * Rotates a 3x3 matrix.
     * @param {number[]} m - Matrix to rotate.
     * @param {number} angle - Angle to rotate by in radians.
     * @param {string} axis - Axis to rotate about (`x`, `y`, or `z`).
     * @returns {number[]} Rotated matrix.
     */
    static rotateMatrix(m, angle, axis) {
        let s = Math.sin(angle);
        let c = Math.cos(angle);
        if (axis === 'x') {
            return [
                m[0], c * m[1] + s * m[2], c * m[2] - s * m[1],
                m[3], c * m[4] + s * m[5], c * m[5] - s * m[4],
                m[6], c * m[7] + s * m[8], c * m[8] - s * m[7]
            ];
        }
        if (axis === 'y') {
            return [
                c * m[0] - s * m[2], m[1], c * m[2] + s * m[0],
                c * m[3] - s * m[5], m[4], c * m[5] + s * m[3],
                c * m[6] - s * m[8], m[7], c * m[8] + s * m[6]
            ];
        }
        if (axis === 'z') {
            return [
                c * m[0] + s * m[1], c * m[1] - s * m[0], m[2],
                c * m[3] + s * m[4], c * m[4] - s * m[3], m[5],
                c * m[6] + s * m[7], c * m[7] - s * m[6], m[8]
            ];
        }
    }

    /**
     * Turns a 3x3 matrix into a 4x4 matrix.
     * @param {number[]} m - Input matrix.
     * @returns {number[]} Expanded matrix.
     */
    static makeMatrix4(m) {
        return [
            m[0], m[1], m[2], 0,
            m[3], m[4], m[5], 0,
            m[6], m[7], m[8], 0,
            0, 0, 0, 1
        ];
    }

    /**
     * Transposes a 4x4 matrix.
     * @param {number[]} m - Input matrix.
     * @returns {number[]} Transposed matrix.
     */
    static transposeMatrix4(m) {
        return [
            m[0], m[4], m[8], m[12],
            m[1], m[5], m[9], m[13],
            m[2], m[6], m[10], m[14],
            m[3], m[7], m[11], m[15]
        ];
    }

    /**
     * Rotates perspective matrix.
     * @param {number[]} p - Perspective matrix.
     * @param {number[]} r - Rotation matrix.
     * @returns {number[]} Rotated matrix.
     */
    static rotatePersp(p, r) {
        return [
            p[0] * r[0], p[0] * r[1], p[0] * r[2], 0,
            p[5] * r[4], p[5] * r[5], p[5] * r[6], 0,
            p[10] * r[8], p[10] * r[9], p[10] * r[10], p[11],
            -r[8], -r[9], -r[10], 0
        ];
    }

    /**
     * Applies rotated perspective matrix to a 3-vector
     * (last element is inverted).
     * @param {number[]} m - Rotated perspective matrix.
     * @param {number[]} v - Input 3-vector.
     * @returns {number[]} Resulting 4-vector.
     */
    static applyRotPerspToVec(m, v) {
        return [
            m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
            m[4] * v[0] + m[5] * v[1] + m[6] * v[2],
            m[11] + m[8] * v[0] + m[9] * v[1] + m[10] * v[2],
            1 / (m[12] * v[0] + m[13] * v[1] + m[14] * v[2])
        ];
    }

    /**
     * Checks if a vertex is visible.
     * @param {number[]} m - Rotated perspective matrix.
     * @param {number[]} v - Input vertex.
     * @returns {number[]} 1 or -1 if the vertex is or is not visible,
     *      respectively.
     */
    static checkInView(m, v) {
        let vpp = CubemapUtil.applyRotPerspToVec(m, v);
        let winX = vpp[0] * vpp[3];
        let winY = vpp[1] * vpp[3];
        let winZ = vpp[2] * vpp[3];
        let ret = [0, 0, 0];

        if (winX < -1)
            ret[0] = -1;
        if (winX > 1)
            ret[0] = 1;
        if (winY < -1)
            ret[1] = -1;
        if (winY > 1)
            ret[1] = 1;
        if (winZ < -1 || winZ > 1)
            ret[2] = 1;
        return ret;
    }



    /**
     * Checks if a square (tile) is visible.
     * @param {number[]} m - Rotated perspective matrix.
     * @param {number[]} v - Square's vertex array.
     * @returns {boolean} Whether or not the square is visible.
     */
    static checkSquareInView(m, v) {
        let check1 = CubemapUtil.checkInView(m, v.slice(0, 3));
        let check2 = CubemapUtil.checkInView(m, v.slice(3, 6));
        let check3 = CubemapUtil.checkInView(m, v.slice(6, 9));
        let check4 = CubemapUtil.checkInView(m, v.slice(9, 12));
        let testX = check1[0] + check2[0] + check3[0] + check4[0];
        if (testX === -4 || testX === 4)
            return false;
        let testY = check1[1] + check2[1] + check3[1] + check4[1];
        if (testY === -4 || testY === 4)
            return false;
        let testZ = check1[2] + check2[2] + check3[2] + check4[2];
        return testZ !== 4;
    }


    /**
     * Creates cube vertex array.
     * @returns {number[]} Cube vertex array.
     */
    static createCube() {
        const helpService = new HelpService();
        const urlParams = helpService.urlParams;
        const edge = urlParams.cube ? urlParams.cube : 1.01;

        return [
            -1, 1, -1, 1, 1, -1, 1, -1, -1, -1, -1, -1, // Front face
            1, 1, edge, -1, 1, edge, -1, -1, edge, 1, -1, edge, // Back face
            -1, 1, edge, 1, 1, edge, 1, 1, -1, -1, 1, -1, // Up face
            -1, -1, -1, 1, -1, -1, 1, -1, edge, -1, -1, edge, // Down face
            -1.0, 1, edge, -1.0, 1, -1, -1.0, -1, -1, -1.0, -1, edge, // Left face
            1, 1, -1, 1, 1, edge, 1, -1, edge, 1, -1, -1  // Right face
        ];
    }

    /**
     * Creates 3x3 identity matrix.
     * @returns {number[]} Identity matrix.
     */
    static identityMatrix3() {
        return [
            1, 0, 0,
            0, 1, 0,
            0, 0, 1
        ];
    }

    static getBoxCoords(yaw, pitch) {
        let cosP = Math.cos(pitch);
        let direction = [Math.sin(yaw) * cosP, -Math.cos(yaw) * cosP, Math.sin(pitch)];
        let intersection = CubemapUtil.PLANES.map(function(plane) {
            let res = intersect.apply(null, [[], [0,0,0], direction].concat(plane));
            if(res && every(res, function(val){return val <= 1;})){
                return res;
            }
            return null;
        }).find(Boolean);
        return intersection && [intersection[0], intersection[2], intersection[1]];
    }
}